import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import AddToCopyGroupSetting, { ADD_TO_COPY_GROUP_SETTING_KEY } from './components/add-to-copy-group-setting';

export const optionKey = 'autoAddInfoFor';

// Attaches the person the field is auto-filled for to the form name as soon as the
// form reaches them, instead of once they have filled it in. Applied on the backend,
// where each recipient's copy of the form is named.
export const ATTACH_ON_INITIATION_KEY = 'attachToFormNameOnInitiation';

export const SUBMITTERS_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'surname', label: 'Last Name' },
  { value: 'position', label: 'Position' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
];

export const AUTO_ADD_INFO_FOR = {
  NONE: 'any',
  INITIATOR: 'initiator',
  SUBMITTER: 'submitter',
  // Strict-order forms only: filled in by whoever submits at step 2. A form
  // without a strict order has a single submitter, so the setting is ignored
  // there — see SUBMITTER_2_STRICT_ORDER_STEP.
  SUBMITTER_2: 'submitter_2',
};

// Zero-based index of the strict-order step that fills AUTO_ADD_INFO_FOR.SUBMITTER_2.
export const SUBMITTER_2_STRICT_ORDER_STEP = 1;

const autoAddInfoOptions = [
  { value: AUTO_ADD_INFO_FOR.NONE, label: '----' },
  { value: AUTO_ADD_INFO_FOR.INITIATOR, label: 'Initiator' },
  { value: AUTO_ADD_INFO_FOR.SUBMITTER, label: 'Submitter' },
  { value: AUTO_ADD_INFO_FOR.SUBMITTER_2, label: 'Submitter 2' },
];

export const SubmittersFieldElementSettings = function FromElement({ element, handlerChangeOption, duplicateElement }) {
  const [label, setLabel] = useState(element.options.labels);
  const [name, setName] = useState(element.options.name);
  const [alignment, setAlignment] = useState(element.options.alignment);
  const [autoAddInfoFor, setAutoAddInfoFor] = useState(element.options?.[optionKey] || autoAddInfoOptions[0]);

  useEffect(() => {
    setLabel(element.options.labels);
    setName(element.options.name);
    setAlignment(element.options.alignment);
    setAutoAddInfoFor(element.options?.[optionKey] || autoAddInfoOptions[0]);
  }, [element]);

  const handleAutoAddInfoChange = (value) => {
    setAutoAddInfoFor(value);
    handlerChangeOption(value?.value === AUTO_ADD_INFO_FOR.NONE ? undefined : value, optionKey, element.id);
  };

  return (
    <div className="tabs-item">
      <div className="property-item">
        <h3 className="form-builder-aside-property-sub-title">Label Alignment</h3>
        <div className="form-group">
          <span className="form-radio-button">
            <input
              type="radio"
              name="alignment"
              value="left"
              checked={alignment === 'left'}
              onChange={(e) => {
                handlerChangeOption(e.target.value, 'alignment', element.id);
                setAlignment(e.target.value);
              }}
            />
            <span>Left</span>
          </span>
          <span className="form-radio-button">
            <input
              type="radio"
              name="alignment"
              value="center"
              checked={alignment === 'center'}
              onChange={(e) => {
                handlerChangeOption(e.target.value, 'alignment', element.id);
                setAlignment(e.target.value);
              }}
            />
            <span>Center</span>
          </span>
          <span className="form-radio-button">
            <input
              type="radio"
              name="alignment"
              value="right"
              checked={alignment === 'right'}
              onChange={(e) => {
                handlerChangeOption(e.target.value, 'alignment', element.id);
                setAlignment(e.target.value);
              }}
            />
            <span>Right</span>
          </span>
        </div>
      </div>
      <div className="property-item">
        <h3 className="form-builder-aside-property-sub-title">Submitters Field Name</h3>
        <div className="form-control-item">
          <input
            className="form-control"
            value={name}
            onChange={(value) => {
              setName(value.target.value);
              handlerChangeOption(value.target.value, 'name', element.id);
            }}
          ></input>
        </div>
      </div>

      <div className="property-item">
        <h3 className="form-builder-aside-property-sub-title">Submitters Field</h3>
        <div className="form-control-item">
          <Select
            classNamePrefix="react-select-multi"
            className="form-control-select"
            hideSelectedOptions={false}
            value={label}
            options={SUBMITTERS_OPTIONS}
            isMulti
            onChange={(value) => {
              setLabel(value);
              handlerChangeOption(value, 'labels', element.id);
            }}
          />
        </div>
      </div>

      <div className="property-item">
        <h3 className="form-builder-aside-property-sub-title">Automatically add info for</h3>
        <div className="form-control-item">
          <Select
            hideSelectedOptions={false}
            className="form-control-select"
            classNamePrefix="react-select"
            value={autoAddInfoFor}
            options={autoAddInfoOptions}
            onChange={handleAutoAddInfoChange}
          />
        </div>
      </div>

      <div className="property-item">
        <h3 className="form-builder-aside-property-sub-title">Attach to the form name</h3>
        <div className="form-control-item d-block">
          <div className="form-control-checkbox w-full">
            <input
              type="checkbox"
              checked={Boolean(element.options.isFormName)}
              onChange={() => {
                const isFormName = !element.options.isFormName;

                handlerChangeOption(isFormName, 'isFormName', element.id);

                // Only a refinement of attaching to the name at all, so it goes with it.
                if (!isFormName && element.options[ATTACH_ON_INITIATION_KEY]) {
                  handlerChangeOption(false, ATTACH_ON_INITIATION_KEY, element.id);
                }
              }}
            />
            <span>Attach to the form name</span>
          </div>
          <div className="form-control-checkbox w-full">
            <input
              type="checkbox"
              disabled={!element.options.isFormName}
              checked={Boolean(element.options.isFormName && element.options[ATTACH_ON_INITIATION_KEY])}
              onChange={() => {
                handlerChangeOption(!element.options[ATTACH_ON_INITIATION_KEY], ATTACH_ON_INITIATION_KEY, element.id);
              }}
            />
            <span>Attach as soon as the form is initiated</span>
          </div>
        </div>
      </div>

      <AddToCopyGroupSetting
        elementID={element?.id}
        selected={element?.options?.[ADD_TO_COPY_GROUP_SETTING_KEY]}
        onChange={handlerChangeOption}
      />
    </div>
  );
};
